import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Copy, ExternalLink } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useToast } from '@/hooks/useToast';
import { LoginArea } from '@/components/auth/LoginArea';
import { useWindowData } from '@/desktop/DesktopContext';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { NoteContent } from '@/components/NoteContent';
import { useDesktop } from '@/desktop/DesktopContext';
import { formatRelativeTime } from '@/lib/formatRelativeTime';

function ProfilePosts({ pubkey }: { pubkey: string }) {
  const { nostr } = useNostr();
  const { openThread } = useDesktop();

  const { data: posts, isLoading } = useQuery({
    queryKey: ['profile-posts', pubkey],
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [{ kinds: [1], authors: [pubkey], limit: 20 }],
        { signal }
      );
      return events.sort((a, b) => b.created_at - a.created_at);
    },
    staleTime: 60000,
  });

  if (isLoading) {
    return (
      <div className="space-y-0 pt-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="px-4 py-3 border-b border-border/40">
            <div className="space-y-1.5">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-sm text-muted-foreground">
        No posts found.
      </div>
    );
  }

  return (
    <div>
      {posts.map((event: NostrEvent) => (
        <button
          key={event.id}
          className="w-full text-left px-4 py-3 border-b border-border/40 hover:bg-muted/20 transition-colors"
          onClick={() => openThread(event)}
        >
          <NoteContent
            event={event}
            className="text-sm leading-relaxed"
            disableNoteEmbeds
            disableMediaEmbeds={false}
          />
          <span className="text-[10px] text-muted-foreground mt-1 block">
            {formatRelativeTime(event.created_at)}
          </span>
        </button>
      ))}
    </div>
  );
}

function ProfileView({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const { toast } = useToast();

  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(pubkey);
  const npub = nip19.npubEncode(pubkey);
  const about = metadata?.about;
  const website = metadata?.website;
  const nip05 = metadata?.nip05;
  const lud16 = metadata?.lud16;

  const copyNpub = () => {
    navigator.clipboard.writeText(npub);
    toast({ title: 'Copied npub to clipboard' });
  };

  if (author.isLoading) {
    return (
      <div className="h-full overflow-auto">
        <div className="relative">
          <Skeleton className="h-32 w-full" />
          <div className="px-4 -mt-10 mb-4">
            <Skeleton className="w-20 h-20 rounded-full border-4 border-background" />
          </div>
          <div className="px-4 pb-4 space-y-3">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <div className="relative">
        {/* Banner */}
        <div className="h-32 bg-gradient-to-br from-primary/20 to-secondary/20">
          {metadata?.banner && (
            <img
              src={metadata.banner}
              alt=""
              className="w-full h-full object-cover"
            />
          )}
        </div>

        {/* Avatar */}
        <div className="px-4 -mt-10 mb-4">
          <Avatar className="w-20 h-20 border-4 border-background">
            <AvatarImage src={metadata?.picture} alt={displayName} />
            <AvatarFallback className="text-2xl">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
        </div>

        <div className="px-4 pb-4 space-y-4">
          <div>
            <h2 className="text-lg font-bold">{displayName}</h2>
            {nip05 && (
              <p className="text-sm text-muted-foreground">{nip05}</p>
            )}
          </div>

          {about && (
            <p className="text-sm text-foreground/80 whitespace-pre-wrap">{about}</p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={copyNpub}>
              <Copy className="w-3.5 h-3.5" />
              Copy npub
            </Button>
            {website && (
              <Button size="sm" variant="outline" className="h-8 gap-1.5" asChild>
                <a href={website} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-3.5 h-3.5" />
                  Website
                </a>
              </Button>
            )}
          </div>

          <div className="space-y-2 pt-2 border-t border-border/50">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Public Key</span>
              <span className="font-mono text-xs truncate max-w-[200px]">{pubkey}</span>
            </div>
            {lud16 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Lightning</span>
                <span className="text-xs">{lud16}</span>
              </div>
            )}
          </div>
        </div>

        {/* Posts */}
        <div className="border-t border-border/50">
          <div className="px-4 py-2 bg-muted/10">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Notes</span>
          </div>
          <ProfilePosts pubkey={pubkey} />
        </div>
      </div>
    </div>
  );
}

export function ProfileApp() {
  const { user } = useCurrentUser();
  const windowData = useWindowData();

  // If window was opened with a specific pubkey (e.g. by clicking a username),
  // show that user's profile. Otherwise fall back to the logged-in user.
  const targetPubkey = (windowData?.pubkey as string | undefined) ?? user?.pubkey;

  if (!targetPubkey) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-sm text-muted-foreground">Login to view your profile</p>
        <LoginArea />
      </div>
    );
  }

  return <ProfileView pubkey={targetPubkey} />;
}
